import React, { useCallback, useEffect, useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import { GramsLabel } from './grams-label';
import { KilogramsLabel } from './kilograms-label';
import { CSWFieldId } from '../../../../constants/field-ids';
import { UnitDisplayType, UnitDisplayTypePropType } from '../../../../enums/units/unit-display-type';
import { UnitPosition, UnitPositionPropType } from '../../../../enums/units/unit-position';
import { WeightNumberInput } from '../weight-number-input';
import { getKilogramsAndGramsFromGrams, combineKilogramsAndGrams } from '../helpers';

// WARNING: This value is used by smoke tests, so if you change this, update the tests accordingly
const GramsTestDataInputType = '(g)';
const KilogramsTestDataInputType = '(kg)';

export const KilogramsAndGramsWeightInput = ({
  inputClassName = null,
  disabled = false,
  shrinkable = false,
  weightValue = null,
  onChange,
  unitDisplayType = UnitDisplayType.AbbreviatedWithParentheses,
  unitPosition = UnitPosition.OutsideEnd,
  numberInputContainerClassName,
  labelId,
  majorUnitPlaceholder = null,
  minorUnitPlaceholder = null,
}) => {
  const weight = useMemo(() => getKilogramsAndGramsFromGrams(weightValue), [weightValue]);
  const [kilogramsWeightValue, setKilogramsWeightValue] = useState(weight.kilograms);
  const [gramsWeightValue, setGramsWeightValue] = useState(weight.grams);

  useEffect(() => {
    const combinedWeight = getKilogramsAndGramsFromGrams(weightValue);
    setKilogramsWeightValue(combinedWeight.kilograms);
    setGramsWeightValue(combinedWeight.grams);
  }, [weightValue]);

  const onGramChange = useCallback((grams) => {
    setGramsWeightValue(grams);
  }, []);

  const onKilogramChange = useCallback((kilograms) => {
    setKilogramsWeightValue(kilograms);
  }, []);

  const onTriggerStateChange = useCallback(() => {
    const updatedWeight = combineKilogramsAndGrams(kilogramsWeightValue, gramsWeightValue);
    onChange(updatedWeight);
  }, [kilogramsWeightValue, gramsWeightValue, onChange]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        onTriggerStateChange();
      }
    },
    [onTriggerStateChange],
  );

  const onBlur = useCallback(() => {
    onTriggerStateChange();
  }, [onTriggerStateChange]);

  const onKilogramArrowButtonPress = useCallback(
    (newKilogramValue) => {
      setKilogramsWeightValue(newKilogramValue);

      const updatedWeight = combineKilogramsAndGrams(newKilogramValue, gramsWeightValue);
      onChange(updatedWeight);
    },
    [gramsWeightValue, onChange],
  );

  const onGramArrowButtonPress = useCallback(
    (newGramValue) => {
      setGramsWeightValue(newGramValue);

      const updatedWeight = combineKilogramsAndGrams(kilogramsWeightValue, newGramValue);
      onChange(updatedWeight);
    },
    [kilogramsWeightValue, onChange],
  );

  return (
    <>
      <WeightNumberInput
        className={inputClassName}
        containerClassName={numberInputContainerClassName}
        disabled={disabled}
        labelId={CSWFieldId.Kilograms}
        min={0}
        onArrowButtonPress={onKilogramArrowButtonPress}
        onBlur={onBlur}
        onChange={onKilogramChange}
        onKeyDown={onKeyDown}
        parentLabelId={labelId}
        placeholder={majorUnitPlaceholder}
        shrinkable={shrinkable}
        testDataInputType={KilogramsTestDataInputType}
        testId='weight-input-kilograms'
        unitPosition={unitPosition}
        value={kilogramsWeightValue}
        weightUnitLabel={<KilogramsLabel unitDisplayType={unitDisplayType} />}
      />
      <WeightNumberInput
        className={inputClassName}
        containerClassName={numberInputContainerClassName}
        disabled={disabled}
        labelId={CSWFieldId.Grams}
        min={0}
        onArrowButtonPress={onGramArrowButtonPress}
        onBlur={onBlur}
        onChange={onGramChange}
        onKeyDown={onKeyDown}
        parentLabelId={labelId}
        placeholder={minorUnitPlaceholder}
        shrinkable={shrinkable}
        testDataInputType={GramsTestDataInputType}
        testId='weight-input-grams'
        unitPosition={unitPosition}
        value={gramsWeightValue}
        weightUnitLabel={<GramsLabel unitDisplayType={unitDisplayType} />}
      />
    </>
  );
};

KilogramsAndGramsWeightInput.propTypes = {
  weightValue: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  shrinkable: PropTypes.bool,
  unitPosition: UnitPositionPropType,
  unitDisplayType: UnitDisplayTypePropType,
  numberInputContainerClassName: PropTypes.string,
  labelId: PropTypes.string,
  majorUnitPlaceholder: PropTypes.string,
  minorUnitPlaceholder: PropTypes.string,
};
